import { buildImageUrl, extractPublicId } from "cloudinary-build-url";
import type { ImageLoaderProps } from "next/image";

export const getUnsplashSrc = (
  src: string,
  type: "REGULAR" | "LQIP",
  customProps: { w?: string; q?: string } = {}
) => {
  const urlObj = new URL(src);

  if (type === "REGULAR") {
    const { w = "1024", q = "60" } = customProps;
    // Mutate query parameters with supplied/default width & quality values.
    urlObj.searchParams.set("w", w);
    urlObj.searchParams.set("q", q);
    urlObj.searchParams.delete("h"); // Remove so it does not affect multi-size image proportions.
  } else if (type === "LQIP") {
    urlObj.searchParams.set("w", "20");
    urlObj.searchParams.set("q", "10");
  }

  return `${urlObj.origin}${urlObj.pathname}?${urlObj.searchParams.toString()}`;
};

/**
 * @link https://cloudinary.com/documentation/image_transformations#named_transformations
 */
export const getCloudinaryNamedSrc = (src: string, transformName: string) => {
  const url = buildImageUrl(extractPublicId(src), {
    cloud: { cloudName: "ekalogs" },
    transformations: { transformation: transformName },
  });
  // example:
  // "res.cloudinary.com/ekalogs/image/upload/q_auto,t_named-w1080,f_auto/ekalogs-assets/geu26ckucx5zazkzygyc"
  return url.replace("q_auto,", "").replace(",f_auto", "");
};

/**
 * images.deviceSizes
 * TODO (later) import value from next.config directly
 */
const RESP_SIZES = [384, 512, 640, 750, 828, 1080, 1200];

export const getCloudinaryDynamicSrc = (src: string, width: number) => {
  const url = buildImageUrl(extractPublicId(src), {
    cloud: { cloudName: "ekalogs" },
    transformations: {
      quality: "auto:eco",
      resize: RESP_SIZES.includes(width) ? { type: "scale", width } : {},
    },
  });
  // example: "res.cloudinary.com/ekalogs/image/upload/c_scale,w_1080,q_auto:eco,f_auto/ekalogs-assets/geu26ckucx5zazkzygyc"
  const queryPath = url.split("image/upload/")[1].split("/")[0];
  const sortedQueryPath = queryPath.split(",").sort().join(",");
  return url.replace(queryPath, sortedQueryPath);
};

export const lqipLoader = (src: string) => {
  // Currently only handling unsplash, everything else is rendered as-is
  if (src.includes("unsplash.com")) {
    return getUnsplashSrc(src, "LQIP");
  } else if (src.includes("res.cloudinary.com/ekalogs")) {
    return getCloudinaryNamedSrc(src, "lqip");
  }
  return src;
};

export const customLoader = ({ src, width }: ImageLoaderProps) => {
  // Image quality is hardcoded to 60 (unsplash), auto:eco (cloudinary).
  // Add the "quality" prop to enable customizing from <Image>.

  if (src.includes("unsplash.com")) {
    return getUnsplashSrc(src, "REGULAR", { w: `${width}` });
  } else if (src.includes("res.cloudinary.com/ekalogs")) {
    return getCloudinaryDynamicSrc(src, width);
  }
  return src;
};

export const markdownImageLoader = ({ width, ...restProps }: ImageLoaderProps) => {
  const maxW = 1200; // No need to generate very large sizes beyond the Markdown container
  return customLoader({
    width: width <= maxW ? width : maxW,
    ...restProps,
  });
};
